import {
	GeometrySourceValue,
	GpsMethodValue,
} from "@/services/shared/geometry-catalog.types";

export const MarkerPopupBodyState = {
	MainLink: "main-link",
	MainNoLink: "main-no-link",
	PointCoordinateEntry: "point-coordinate-entry",
	PointDeviceGps: "point-device-gps",
	PointBluetoothRtk: "point-bluetooth-rtk",
} as const;

export type MarkerPopupBodyState =
	(typeof MarkerPopupBodyState)[keyof typeof MarkerPopupBodyState];

/**
 * Legacy Figma / story labels for Point details panels.
 * When geometrySource is GPS, runtime Source shows gpsMethod (Device GPS / Bluetooth RTK).
 */
export const MarkerPopupPointSource = {
	CoordinateEntry: "Coordinate Entry",
	DeviceGps: GpsMethodValue.DeviceGps,
	BluetoothRtk: GpsMethodValue.BluetoothRtk,
} as const;

export type MarkerPopupPointSource =
	(typeof MarkerPopupPointSource)[keyof typeof MarkerPopupPointSource];

/**
 * Optional survey / GPS fields for MarkerPopup Point details.
 * Omitted values render as EmptyValue in the point-details body.
 *
 * `source` = geometrySource for non-GPS, or gpsMethod when geometrySource is GPS.
 * `gpsMethod` is optional legacy/panel hint; prefer encoding method in `source` for GPS.
 */
export interface MarkerPopupPointDetails {
	source?: string;
	gpsMethod?: string;
	latitude?: number;
	longitude?: number;
	elevation?: string;
	timestamp?: string;
	accuracy?: string;
	vertex?: string;
	horizontalAccuracy?: string;
	verticalAccuracy?: string;
	fixType?: string;
	satelliteCount?: string;
	hdop?: string;
	depth?: string;
	current?: string;
	frequency?: string;
	gain?: string;
}

/**
 * Builds the Device GPS payload used by map popups (lat/lng only until API fields exist).
 */
export function createDeviceGpsPointDetails(
	latitude: number,
	longitude: number,
): MarkerPopupPointDetails {
	return {
		source: GpsMethodValue.DeviceGps,
		latitude,
		longitude,
	};
}

function isNonGpsGeometrySource(source: string | undefined): boolean {
	if (source == null || source.trim() === "") return false;
	const normalized = source.toLowerCase().trim();
	return (
		normalized === GeometrySourceValue.ManualEntry.toLowerCase() ||
		normalized === GeometrySourceValue.Geocoded.toLowerCase() ||
		normalized === GeometrySourceValue.MapDraw.toLowerCase() ||
		normalized === MarkerPopupPointSource.CoordinateEntry.toLowerCase()
	);
}

function isBluetoothRtkMethod(value: string | undefined): boolean {
	return (
		value?.toLowerCase().trim() === GpsMethodValue.BluetoothRtk.toLowerCase()
	);
}

/**
 * Resolves Figma Body State for a Point details payload.
 * Non-GPS geometry sources use Coordinate Entry; GPS panels follow method in `source`
 * (or legacy `gpsMethod`).
 */
export function resolvePointBodyState(
	pointDetails: MarkerPopupPointDetails,
): MarkerPopupBodyState {
	if (isNonGpsGeometrySource(pointDetails.source)) {
		return MarkerPopupBodyState.PointCoordinateEntry;
	}
	if (
		isBluetoothRtkMethod(pointDetails.source) ||
		isBluetoothRtkMethod(pointDetails.gpsMethod)
	) {
		return MarkerPopupBodyState.PointBluetoothRtk;
	}
	return MarkerPopupBodyState.PointDeviceGps;
}
